<template>
  <main class="auth-form-page">
    <AppNavBar :border="false" />

    <section class="auth-form-head">
      <h1>{{ t("auth.resetPaymentPassword") }}</h1>
      <p>{{ t("auth.resetPaymentPasswordTip") }}</p>
    </section>

    <section v-if="!email" class="auth-empty-card">
      <div data-strong>{{ t("auth.noBoundEmailRecover") }}</div>
      <p>{{ t("auth.bindEmailTip") }}</p>
      <AuthThemeButton
        block
        class="auth-empty-card__action"
        @click="goBindEmail"
      >
        {{ t("auth.goBindEmail") }}
      </AuthThemeButton>
    </section>

    <template v-else>
      <section class="auth-form-section">
        <label class="auth-form-label">
          {{ t("auth.newPassword") }} <em>*</em>
        </label>
        <van-field
          v-model="password"
          class="auth-form-field"
          :type="showPassword ? 'text' : 'password'"
          clearable
          :placeholder="t('auth.enterNewPassword')"
        >
          <template #button>
            <button
              type="button"
              class="auth-eye-btn"
              @click="showPassword = !showPassword"
            >
              <app-image :src="showPassword ? eyeOpenIcon : eyeCloseIcon" alt="" />
            </button>
          </template>
        </van-field>
        <div
          v-if="password"
          class="strength-row"
          :class="`strength-row--${strengthLevel}`"
        >
          <div class="strength-row-line" v-show="password.length > 7">
            <span
              v-for="item in 3"
              :key="item"
              :class="{ active: item <= strengthLevel }"
            />
          </div>
          <div data-strong :class="`level-${strengthLevel} ${password.length < 8 ? 'red' : ''}`">{{ strengthText }}</div>
        </div>
      </section>

      <section class="auth-form-section">
        <label class="auth-form-label">
          {{ t("auth.confirmNewPassword") }} <em>*</em>
        </label>
        <van-field
          v-model="passwordAgain"
          class="auth-form-field"
          :class="{
            'auth-form-field--error':
              passwordAgain && passwordAgain !== password,
          }"
          :type="showPasswordAgain ? 'text' : 'password'"
          clearable
          :placeholder="t('auth.enterNewPassword')"
        >
          <template #button>
            <button
              type="button"
              class="auth-eye-btn"
              @click="showPasswordAgain = !showPasswordAgain"
            >
              <app-image
                :src="showPasswordAgain ? eyeOpenIcon : eyeCloseIcon"
                alt=""
              />
            </button>
          </template>
        </van-field>
        <div v-if="passwordAgain" class="strength-row" :class="`strength-row--${strengthLevelAgain}`">
        <div class="strength-row-line" v-show="passwordAgain.length > 7">
          <span v-for="item in 3" :key="item" :class="{ active: item <= strengthLevelAgain }" />
        </div>
          <div data-strong :class="`level-${strengthLevelAgain} ${passwordAgain.length < 8 ? 'red' : ''}`">{{ strengthTextAgain }}</div>
        </div>
      </section>

      <section class="auth-form-section">
        <label class="auth-form-label">{{ t("auth.boundEmail") }}</label>
        <van-field
          :model-value="email"
          class="auth-form-field mt-20"
          readonly
        />
        <van-field
          v-model="emailCode"
          class="auth-form-field"
          clearable
          maxlength="8"
          :placeholder="t('auth.emailCode')"
        >
          <template #button>
            <button
              type="button"
              class="auth-code-btn"
              :disabled="!email || countdown > 0"
              @click="sendCode"
            >
              {{
                countdown > 0
                  ? `${t("auth.resend")}${countdown}s`
                  : t("auth.getCode")
              }}
            </button>
          </template>
        </van-field>
      </section>

      <AuthThemeButton
        block
        :disabled="passwordDisabled"
        class="auth-submit-btn"
        :loading="submitting"
        @click="submitReset"
      >
        {{ t("common.confirm") }}
      </AuthThemeButton>
    </template>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { showFailToast, showToast } from 'vant';
import { showPassedToast } from '@/utils/toast';
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { changePayPwd, devicesInfo, emailSend } from "@/api/auth";
import AuthThemeButton from "@/view/auth/components/AuthThemeButton.vue";
import { useProfileStore } from "@/stores/profile";
import { getDeviceId } from "@/utils/device";
import { navigateWithState } from "@/utils/route-state";
import { saveWalletPassword } from "@/utils/walletSession";
import eyeOpenIcon from "@/assets/images/eye_open_icon.png";
import eyeCloseIcon from "@/assets/images/eye_close_icon.png";

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();

const password = ref("");
const passwordAgain = ref("");
const email = ref("");
const emailCode = ref("");
const showPassword = ref(false);
const showPasswordAgain = ref(false);
const submitting = ref(false);
const countdown = ref(0);
let timer = null;

const strengthLevel = computed(() => {
  const value = password.value;
  if (value.length < 8) return 0;
  let count = 0;
  if (/[A-Z]/.test(value)) count += 1;
  if (/[a-z]/.test(value)) count += 1;
  if (/\d/.test(value)) count += 1;
  if (/[!@#$&*~^%()\-_+=.,;:]/.test(value)) count += 1;
  if (value.length >= 12 && count === 4) return 3;
  if (value.length >= 12 && count >= 3) return 2;
  return 1;
});

const strengthText = computed(() => {
  if (strengthLevel.value === 3) return t("auth.passwordStrong");
  if (strengthLevel.value === 2) return t("auth.passwordMedium");
  if (strengthLevel.value === 1) return t("auth.passwordWeak");
  return t("auth.passwordTooShort");
});

const strengthLevelAgain = computed(() => {
  const value = passwordAgain.value;
  if (value.length < 8) return 0;
  let count = 0;
  if (/[A-Z]/.test(value)) count += 1;
  if (/[a-z]/.test(value)) count += 1;
  if (/\d/.test(value)) count += 1;
  if (/[!@#$&*~^%()\-_+=.,;:]/.test(value)) count += 1;
  if (value.length >= 12 && count === 4) return 3;
  if (value.length >= 12 && count >= 3) return 2;
  return 1;
});
const strengthTextAgain = computed(() => {
  if (strengthLevelAgain.value === 3) return t('auth.passwordStrong');
  if (strengthLevelAgain.value === 2) return t('auth.passwordMedium');
  if (strengthLevelAgain.value === 1) return t('auth.passwordWeak');
  return t('auth.passwordTooShort');
});
const passwordDisabled = computed(() => !password.value || !passwordAgain.value);

function startCountdown() {
  countdown.value = 60;
  window.clearInterval(timer);
  timer = window.setInterval(() => {
    countdown.value -= 1;
    if (countdown.value <= 0) {
      window.clearInterval(timer);
      timer = null;
    }
  }, 1000);
}

async function loadBoundEmail() {
  const cachedEmail = String(profileStore.currentAccount?.email || "").trim();
  if (cachedEmail) {
    email.value = cachedEmail;
    return;
  }

  try {
    const result = await devicesInfo(
      { token: getDeviceId() },
      { meta: { showLoading: false } }
    );
    email.value = String(result?.email || "").trim();
    if (email.value) {
      profileStore.setCurrentAccount({
        ...profileStore.currentAccount,
        email: email.value,
      });
    }
  } catch (_) {
    // ignore
  }
}

function goBindEmail() {
  navigateWithState(router, "/auth/bind-email", {
    namespace: "auth-bind-email",
    payload: {
      back: "/auth/forgot-pay-password"
    }
  });
}

async function sendCode() {
  if (!email.value) {
    showFailToast(t("auth.bindEmailTip"));
    return;
  }

  try {
    await emailSend({
      token: getDeviceId(),
      email: email.value,
      type: "FORGET",
    });
    showPassedToast(t("auth.codeSent"));
    startCountdown();
  } catch (_) {
    // handled in interceptor
  }
}

async function submitReset() {
  if (!email.value) {
    showFailToast(t("auth.bindEmailTip"));
    return;
  }
  if (password.value.length < 8 || passwordAgain.value.length < 8) {
    showFailToast(t("auth.passwordTooShort"));
    return;
  }
  if (!passwordAgain.value.trim()) {
    showFailToast(t("auth.confirmPassword"));
    return;
  }
  if (passwordAgain.value !== password.value) {
    showFailToast(t("auth.passwordMismatch"));
    return;
  }
  if (!emailCode.value.trim()) {
    showFailToast(t("auth.codeRequired"));
    return;
  }

  submitting.value = true;
  try {
    await changePayPwd({
      payPwd: password.value.trim(),
      code: emailCode.value.trim(),
    });
    if (profileStore.accountStorageAddress) {
      saveWalletPassword(
        profileStore.accountStorageAddress,
        password.value.trim()
      );
    }
    showPassedToast(t("auth.changeSuccess"));
    setTimeout(() => {
      router.back();
    }, 1000)
  } catch (_) {
    // handled in interceptor
  } finally {
    submitting.value = false;
  }
}

onMounted(async () => {
  await loadBoundEmail();
});

onBeforeUnmount(() => {
  window.clearInterval(timer);
});
</script>

<style scoped lang="scss">
@use "@/styles/auth-form.scss";

.mt-20 {
  margin-bottom: 20px;
}

.auth-empty-card {
  margin-top: 28px;
  padding: 28px 18px;
  border-radius: 16px;
  background: #f7f8fa;
  text-align: center;

  [data-strong] {
    display: block;
    color: #1d2129;
    font-size: 18px;
    font-weight: 500;
    line-height: 26px;
  }

  p {
    margin: 10px 0 0;
    color: #4e5969;
    font-size: 13px;
    line-height: 20px;
  }
}

.auth-empty-card__action {
  margin-top: 22px;
}

.strength-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;

  .strength-row-line {
    display: flex;
    gap: 10px;

    span {
      width: 50px;
      height: 5px;
      border-radius: 16px;
      background: #c9cdd4;
    }

    span.active {
      background: #ff7d00;
    }
  }

  [data-strong] {
    flex: 0 0 auto;
    font-size: 13px;
    font-weight: 500;
  }

  .red {
    color: red;
  }


  .level-1 {
    color: #ff7d00;
  }

  .level-2,
  .level-3 {
    color: #07b077;
  }
}

.strength-row--2 .strength-row-line span.active,
.strength-row--3 .strength-row-line span.active {
  background: #07b077 !important;
}
</style>
